import { useMemo } from "react";
import { useUser } from "@/auth/useUser";
import { AppointmentServiceInput } from "@/components/booking/multiProviderAppointmentDrawer/multiProviderAppointmentDrawerContext";
import { VIOLET } from "@/config/mui/colorPalette";
import { useAppointmentsAndAvailabilityEventsWithDateRangeQuery } from "@/graphql/queries/appointment/appointmentsAndAvailabilityEventsWithDateRange.graphql.types";
import { useMultiProviderAppointmentsAndAvailabilityEventsWithDateRangeQuery } from "@/graphql/queries/appointment/multiProviderAppointmentsAndAvailabilityEventsWithDateRange.graphql.types";
import { MedspaProvidersQuery } from "@/graphql/queries/medspaProviders.graphql.types";
import {
  ACTIVE_APPOINTMENT_STATUSES,
  ReviewStatus,
  AppointmentStatus,
  AppointmentType,
} from "@/types";
import { ExtendedEventProps } from "@/types/calendarEventType";
import {
  getEndTime,
  getEventColorsByState,
  getUniqServicesProviders,
} from "@/utils/appointment";
import useFeatureFlags from "../common/useFeatureFlags";
import useAppointmentEvents, {
  useCalendarAppointmentServices,
} from "./useAppointmentEvents";
import useAvailabilityEvents from "./useAvailabilityEvents";
import { useDateToMedspaWeekRange } from "./useDateToMedspaWeekRange";

type ServiceWithDuration = {
  id: string;
  durationInMinutes: number;
};

export default function useCreateViewCalendarEvents({
  start,
  end,
  providers,
  availableServices,
  client,
  excludeAppointmentId,
  currentServicesLabel,
  currentAppointmentServices,
}: {
  start?: Date;
  end?: Date;
  providers?: MedspaProvidersQuery["medspaByPk"]["userMedspas"];
  availableServices?: ServiceWithDuration[];
  client?: { firstName: string; lastName: string };
  excludeAppointmentId?: string;
  currentServicesLabel?: string;
  currentAppointmentServices?: AppointmentServiceInput[];
}) {
  const { multiProviderAppointmentsV1Enabled } = useFeatureFlags();
  const { medspa } = useUser();
  const dateRange = useDateToMedspaWeekRange(start ?? new Date());

  const providerIds =
    providers?.map((provider) => provider?.user.id).filter(Boolean) ?? [];

  const { data: oldData } =
    useAppointmentsAndAvailabilityEventsWithDateRangeQuery({
      variables: {
        medspaId: medspa,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        status: ACTIVE_APPOINTMENT_STATUSES,
        providers: providerIds,
        providerFilterExp: { providerId: { _in: providerIds } },
        gfeStatusesExp: {},
        serviceDevicesExp: {},
      },
      skip:
        !start ||
        !providers?.length ||
        !medspa ||
        !dateRange ||
        multiProviderAppointmentsV1Enabled,
      fetchPolicy: "cache-and-network",
    });

  const { data: newData } =
    useMultiProviderAppointmentsAndAvailabilityEventsWithDateRangeQuery({
      variables: {
        medspaId: medspa,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        status: ACTIVE_APPOINTMENT_STATUSES,
        providers: providers?.map((provider) => provider?.id),
        gfeStatusesExp: {},
        serviceDevicesExp: {},
      },
      skip:
        !start ||
        !providers?.length ||
        !medspa ||
        !dateRange ||
        !multiProviderAppointmentsV1Enabled,
      fetchPolicy: "cache-and-network",
    });

  const data = multiProviderAppointmentsV1Enabled ? newData : oldData;

  const appointmentEvents = useAppointmentEvents(data?.appointment);
  const { timeBlockEvents, specialShiftsEvents } = useAvailabilityEvents(
    data?.availabilityEvent
  );

  const existingEvents = useMemo(() => {
    const plannedAppointments =
      appointmentEvents
        .filter(({ id }) => id !== excludeAppointmentId)
        .map(({ extendedProps, ...appointment }, i) => {
          return {
            ...appointment,
            end:
              appointment.end ||
              getEndTime(data.appointment[i], availableServices),
            extendedProps: {
              ...extendedProps,
              showDropdown: false,
              clickable: false,
            },
          };
        }) || [];

    const timeBlockers = timeBlockEvents.map(({ extendedProps, ...event }) => ({
      ...event,
      extendedProps: {
        ...extendedProps,
        showDropdown: false,
        clickable: false,
      },
    }));

    const specialShifts = specialShiftsEvents.map(
      ({ extendedProps, ...event }) => ({
        ...event,
        extendedProps: {
          ...extendedProps,
          showDropdown: false,
        },
      })
    );

    return [...plannedAppointments, ...timeBlockers, ...specialShifts].map(
      (event) => ({
        ...event,
        editable: false,
      })
    );
  }, [
    appointmentEvents,
    availableServices,
    excludeAppointmentId,
    data?.appointment,
    specialShiftsEvents,
    timeBlockEvents,
  ]);

  const { mapAppointmentServicesToCalendar } = useCalendarAppointmentServices();

  const currentFormEvents = useMemo(() => {
    const extendedProps: ExtendedEventProps = {
      isTimeBlock: false,
      isSpecialShift: false,
      showDropdown: false,
      status: AppointmentStatus.SCHEDULED,
      reviewStatus: ReviewStatus.INCOMPLETE,
      services: currentServicesLabel,
      isCreatingEvent: true,
      clientMembershipsList: "",
    };

    const currentEventTitle = `${client?.firstName} ${client?.lastName}`;

    if (currentAppointmentServices) {
      const eventColorsByState = getEventColorsByState({
        status: extendedProps.status,
        multiProviderAppointmentsV1Enabled: true,
      });

      return [
        {
          start: currentAppointmentServices[0].startTime.toISOString(),
          end: currentAppointmentServices.at(-1).endTime.toISOString(),
          title: currentEventTitle,
          durationEditable: false,
          backgroundColor: eventColorsByState.backgroundColor,
          borderColor: eventColorsByState.borderColor,
          textColor: eventColorsByState.textColor,
          extendedProps: {
            ...extendedProps,
            appointmentServices: mapAppointmentServicesToCalendar(
              currentAppointmentServices.map((item) => ({
                ...item,
                startTime: item.startTime.toISOString(),
                endTime: item.endTime.toISOString(),
              }))
            ),
            servicesProviders: getUniqServicesProviders(
              currentAppointmentServices
            ),
            services: currentAppointmentServices
              .map((item) => item.serviceMenuItem.name)
              .join(" • "),
            isAppointmentTelehealth: currentAppointmentServices.some(
              (item) =>
                item.serviceMenuItem.appointmentType ===
                AppointmentType.TELEHEALTH
            ),
          },
        },
      ];
    }

    const currentAppointmentEvent = {
      start: start?.toISOString(),
      end: end?.toISOString(),
      title: currentEventTitle,
      backgroundColor: "#fbf5ff",
      borderColor: VIOLET[90],
      textColor: VIOLET[90],
      editable: true,
      extendedProps,
    };

    return [currentAppointmentEvent];
  }, [
    currentAppointmentServices,
    start,
    end,
    client,
    currentServicesLabel,
    mapAppointmentServicesToCalendar,
  ]);

  return useMemo(
    () => [...existingEvents, ...currentFormEvents],
    [existingEvents, currentFormEvents]
  );
}
